import React, {useState, useEffect, useRef} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useDispatch} from "react-redux";

import {useProfile} from "../Components/Hooks/UserHooks";
import {getProjects as onGetProject} from "../slices/thunks";

const NavData = () => {
    const {userProfile, loading, isAdmin, isSuperUser} = useProfile();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [projects, setProjects] = useState([]);
    const menuLinksRef = useRef([]);

    // State data for top level items
    const [isCurrentState, setIsCurrentState] = useState("Sales");
    const [isSales, setIsSales] = useState(false);
    const [isInventory, setIsInventory] = useState(false);
    const [isReporting, setIsReporting] = useState(false);
    const [isVendor, setIsVendor] = useState(false);
    const [isAdminPanel, setIsAdminPanel] = useState(false);
    const [isManagement, setIsManagement] = useState(false);
    // Sales
    const [isClients, setIsClients] = useState(false);
    // Inventory
    const [isProjects, setIsProjects] = useState({});
    const [isPhases, setIsPhases] = useState({});
    const [isBlocks, setIsBlocks] = useState({});

    // Track active states
    const [activeItem, setActiveItem] = useState("");

    // Build a list of all menu links after projects are loaded
    useEffect(() => {
        if (projects?.length > 0) {
            const links = [// Static menu paths
                "/financial/clients", "/financial/contracts", "/financial/invoices/overdue"];

            // Add block links
            projects.forEach(project => {
                links.push(`/project-reporting/${project.id}/budget`)
                if (project.phases) {
                    project.phases.forEach(phase => {
                        if (phase.blocks) {
                            phase.blocks.forEach(block => {
                                links.push(`/units-by-block/${block.id}`);
                            });
                        }
                    });
                }
            });

            menuLinksRef.current = links;
        }
    }, [projects]);

    // Function to reset all states
    const resetAllStates = () => {
        setIsSales(false);
        setIsInventory(false);
        setIsReporting(false);
        setIsVendor(false);
        setIsAdminPanel(false);
        setIsManagement(false);
        setIsClients(false);

        // Reset project states
        const resetProjectStates = {};
        Object.keys(isProjects).forEach(key => {
            resetProjectStates[key] = false;
        });
        setIsProjects(resetProjectStates);

        // Reset phase states
        const resetPhaseStates = {};
        Object.keys(isPhases).forEach(key => {
            resetPhaseStates[key] = false;
        });
        setIsPhases(resetPhaseStates);

        // Reset block states
        const resetBlockStates = {};
        Object.keys(isBlocks).forEach(key => {
            resetBlockStates[key] = false;
        });
        setIsBlocks(resetBlockStates);
    };

    useEffect(() => {
        const path = location.pathname;

        // Skip state reset for non-menu paths
        const isMenuPath = menuLinksRef.current.some(link => path.includes(link));
        if (!isMenuPath) {
            return;
        }

        // If this is a block page, ensure the right navigation is active
        if (path.includes('/units-by-block/')) {
            const blockMatch = path.match(/\/units-by-block\/(\d+)/);
            if (blockMatch && blockMatch[1]) {
                const blockId = blockMatch[1];
                setActiveItem(`block-${blockId}`);

                // Find the project and phase for this block
                let foundProject = null;
                let foundPhase = null;

                // Loop through projects to find which one contains this block
                projects.forEach(project => {
                    if (project.phases) {
                        project.phases.forEach(phase => {
                            if (phase.blocks) {
                                const matchingBlock = phase.blocks.find(block => block.id.toString() === blockId);
                                if (matchingBlock) {
                                    foundProject = project;
                                    foundPhase = phase;
                                }
                            }
                        });
                    }
                });

                // If we found the project and phase, make sure they're expanded
                if (foundProject && foundPhase) {
                    setIsCurrentState("Inventory");
                    setIsInventory(true);

                    setIsProjects(prevState => ({
                        ...prevState, [foundProject.id]: true
                    }));

                    setIsPhases(prevState => ({
                        ...prevState, [foundPhase.id]: true
                    }));
                }
            }

            return;
        }

        resetAllStates();

        // Automatically expand sections based on URL path
        if (path.includes("/clients")) {
            setIsCurrentState("Sales");
            setIsSales(true);
            setIsClients(true);

            const params = new URLSearchParams(window.location.search);
            const buyerType = params.get("buyer_type");
            if (buyerType === "natural") {
                setActiveItem("clients-natural");
            } else if (buyerType === "legal") {
                setActiveItem("clients-legal");
            }
        } else if (path.includes("/projects/")) {
            setIsCurrentState("Inventory");
            setIsInventory(true);

            // Extract project ID if present
            const projectMatch = path.match(/\/projects\/(\d+)/);
            if (projectMatch && projectMatch[1]) {
                const projectId = projectMatch[1];
                setActiveItem(`project-${projectId}`);

                // Set the specific project's state to true if it exists
                if (isProjects[projectId] !== undefined) {
                    setIsProjects(prevState => ({
                        ...prevState, [projectId]: true
                    }));
                }
            }
        } else if (path.includes("/financial/contracts")) {
            setIsCurrentState("Sales");
            setIsSales(true);
            setActiveItem("contracts");
        } else if (path.includes("/project-reporting/")) {
            setIsCurrentState("Reporting");
            setIsReporting(true);

            // Extract project ID for project reporting
            const projectMatch = path.match(/\/project-reporting\/(\d+)/);
            if (projectMatch && projectMatch[1]) {
                setActiveItem(`project-reporting-${projectMatch[1]}`);
            }
        } else if (path.includes("/invoices/overdue")) {
            setIsCurrentState("Reporting");
            setIsReporting(true);
            setActiveItem("overdue");
        } else if (path.includes("/admin/panel")) {
            setIsCurrentState("Admin");
            setIsAdminPanel(true);
            setActiveItem("user-management");
        } else if (["/projects", "/phases", "/blocks"].includes(path)) {
            setIsCurrentState("Management");
            setIsManagement(true);
            setActiveItem(
                path === "/projects" ? "projects-management" :
                    path === "/phases" ? "phases-management" :
                        "blocks-management"
            );
        }
    }, [location.pathname, projects]);

    function updateIconSidebar(e) {
        if (e && e.target && e.target.getAttribute("subitems")) {
            const ul = document.getElementById("two-column-menu");
            if (ul) {
                const iconItems = ul.querySelectorAll(".nav-icon.active");
                let activeIconItems = [...iconItems];
                activeIconItems.forEach((item) => {
                    item.classList.remove("active");
                    const id = item.getAttribute("subitems");
                    if (document.getElementById(id)) document.getElementById(id).classList.remove("show");
                });
            }
        }
    }

    useEffect(() => {
        const fetchProjects = async () => {
            if (!loading) {
                try {
                    const response = await dispatch(onGetProject());
                    const projects_list = response.payload;
                    setProjects(projects_list);

                    // Initialize project states
                    const initialProjectStates = {};
                    projects_list.forEach(project => {
                        initialProjectStates[project.id] = false;
                    });
                    setIsProjects(initialProjectStates);

                    // Initialize phase states
                    const initialPhaseStates = {};
                    projects_list.forEach(project => {
                        if (project.phases) {
                            project.phases.forEach(phase => {
                                initialPhaseStates[phase.id] = false;
                            });
                        }
                    });
                    setIsPhases(initialPhaseStates);

                    // Initialize block states
                    const initialBlockStates = {};
                    projects_list.forEach(project => {
                        if (project.phases) {
                            project.phases.forEach(phase => {
                                if (phase.blocks) {
                                    phase.blocks.forEach(block => {
                                        initialBlockStates[block.id] = false;
                                    });
                                }
                            });
                        }
                    });
                    setIsBlocks(initialBlockStates);
                } catch (error) {
                }
            }
        };
        // Call the function without returning its result
        void fetchProjects();
    }, [loading, dispatch]);

    // This effect runs when isCurrentState changes
    useEffect(() => {
        document.body.classList.remove("twocolumn-panel");

        // Reset all states first
        resetAllStates();

        // Set the appropriate state based on current selection
        switch (isCurrentState) {
            case "Sales":
                setIsSales(true);
                break;
            case "Inventory":
                setIsInventory(true);
                break;
            case "Reporting":
                setIsReporting(true);
                break;
            case "Vendors":
                setIsVendor(true);
                break;
            case "AdminPanel":
                setIsAdminPanel(true);
                break;
            case "Management":
                setIsManagement(true);
                break;
            default:
                break;
        }

    }, [isCurrentState]);

    // Define menu items with proper subItems
    const menuItems = [{
        label: "Menu", isHeader: true
    }, {
        id: "sales", label: "Sales", icon: "mdi mdi-storefront", stateVariables: isSales, click: function (e) {
            e.preventDefault();
            setIsSales(!isSales);
            setIsCurrentState("Sales");
            updateIconSidebar(e);
        }, subItems: [{
            id: "clients", label: "Clients", link: "/#", isChildItem: true, click: function (e) {
                e.preventDefault();
                setIsClients(!isClients);
                updateIconSidebar(e);
            }, stateVariables: isClients, parentId: "sales", childItems: [{
                id: "clients-natural", label: "Natural Clients", link: "/financial/clients?buyer_type=natural", active: activeItem === "clients-natural", click: function (e) {
                    e.preventDefault();
                    setActiveItem("clients-natural");
                    navigate("/financial/clients?buyer_type=natural");
                }
            }, {
                id: "clients-legal", label: "Legal Clients", link: "/financial/clients?buyer_type=legal", active: activeItem === "clients-legal", click: function (e) {
                    e.preventDefault();
                    setActiveItem("clients-legal");
                    navigate("/financial/clients?buyer_type=legal");
                }
            },]
        }, {
            id: "contracts", label: "Contracts", link: "/financial/contracts", active: activeItem === "contracts", click: function (e) {
                setActiveItem("contracts");
            }
        },
        {
            id: "Invoices", label: "Invoices", link: "/financial/invoices", active: activeItem === "invoices", click: function (e) {
                setActiveItem("invoices");
            }
        }]
    }, {
        id: "inventory", label: "Inventory", icon: "bx bx-buildings", stateVariables: isInventory, click: function (e) {
            e.preventDefault();
            setIsInventory(!isInventory)
            setIsCurrentState("Inventory");
            updateIconSidebar(e);
        }, subItems: projects?.map(project => ({
            id: `project-${project.id}`,
            label: project.name || "Project",
            active: activeItem === `project-${project.id}`,
            stateVariables: isProjects[project.id],
            link: "/#",
            parentId: "inventory",
            isChildItem: true,
            click: function (e) {
                e.preventDefault();
                setIsProjects(prevState => ({
                    ...prevState, [project.id]: !prevState[project.id]
                }));
                setActiveItem(`project-${project.id}`);
            },
            childItems: project.phases && project.phases.map(phase => ({
                id: `phase-${phase.id}`,
                label: phase.name || "Phase",
                active: activeItem === `phase-${phase.id}`,
                stateVariables: isPhases[phase.id],
                link: "/#",
                parentId: `project-${project.id}`,
                isChildItem: true,
                click: function (e) {
                    e.preventDefault();
                    setIsPhases(prevState => ({
                        ...prevState, [phase.id]: !prevState[phase.id]
                    }));
                    setActiveItem(`phase-${phase.id}`);
                },
                childItems: phase.blocks && phase.blocks.map(block => ({
                    id: `block-${block.id}`,
                    label: block.name || "Block",
                    link: `/units-by-block/${block.id}`,
                    active: activeItem === `block-${block.id}`,
                    parentId: `phase-${phase.id}`,
                    click: function (e) {
                        e.preventDefault();

                        // Set this block as active
                        setActiveItem(`block-${block.id}`);

                        // Make sure the parent project and phase stay expanded
                        setIsProjects(prevState => ({
                            ...prevState, [project.id]: true
                        }));

                        setIsPhases(prevState => ({
                            ...prevState, [phase.id]: true
                        }));

                        // Now navigate
                        navigate(`/units-by-block/${block.id}`);
                    }
                }))
            }))
        }))
    },
        {
            id: "reporting",
            label: "Reporting",
            icon: "ri-bar-chart-line",
            stateVariables: isReporting,
            click: function (e) {
                e.preventDefault();
                setIsReporting(!isReporting);
                setIsCurrentState("Reporting");
                updateIconSidebar(e);
            }, subItems: [
                ...((isAdmin || isSuperUser) && projects && Array.isArray(projects)
                    ? projects.map((project) => ({
                        id: `project-reporting-${project.id}`,
                        label: project.name,
                        link: `/project-reporting/${project.id}/overview`,
                        active: activeItem === `project-reporting-${project.id}`,
                        click: function (e) {
                            setActiveItem(`project-reporting-${project.id}`);
                        }
                    }))
                    : []),
                {
                    id: "overdue",
                    label: "Overdue",
                    link: "/invoices/overdue",
                    active: activeItem === "overdue",
                    click: function (e) {
                        setActiveItem("overdue");
                    }
                }
            ]
        },
        {
            id: "vendors",
            label: "Vendors",
            icon: "ri-archive-line",
            stateVariables: isVendor,
            link: `/financial/invoices/vendors`,
            click: function (e) {
                e.preventDefault();
                setIsVendor(!isVendor);
                setIsCurrentState("Vendors");
                updateIconSidebar(e);
            },
        },
        ...(isAdmin || isSuperUser
            ? [
                {
                    label: "Admin",
                    isHeader: true,
                },
                {
                    id: "management",
                    label: "Management",
                    icon: "mdi mdi-cogs",
                    click: (e) => {
                        e.preventDefault();
                        setIsManagement(!isManagement);
                        setIsCurrentState("Management");
                        updateIconSidebar(e);
                    },
                    stateVariables: isManagement,
                    subItems: [
                        {
                            id: "projects-management",
                            label: "Projects",
                            icon: "mdi mdi-folder-multiple",
                            link: "/projects",
                        },
                        {
                            id: "phases-management",
                            label: "Phases",
                            icon: "mdi mdi-calendar-multiple",
                            link: "/phases",
                        },
                        {
                            id: "blocks-management",
                            label: "Blocks",
                            icon: "mdi mdi-cube-outline",
                            link: "/blocks",
                        },
                    ],
                },
                {
                    id: "admin-panel",
                    label: "Admin Panel",
                    icon: "mdi mdi-account-circle",
                    link: "/admin/panel",
                    stateVariables: isAdminPanel,
                    click: (e) => {
                        setIsCurrentState("AdminPanel");
                        setIsAdminPanel(true);
                        updateIconSidebar(e);
                    },
                    subItems: [
                        {
                            id: "user-management",
                            label: "User Management",
                            icon: "mdi mdi-account-cog",
                            link: "/admin/panel/users",
                            parentId: "admin-panel",
                        },
                        {
                            id: "accounts-management",
                            label: "Accounts Management",
                            icon: "mdi mdi-account-cog",
                            link: "/admin/panel/accounts",
                            parentId: "admin-panel",
                        },
                        {
                            id: "cost-categories",
                            label: "Cost Categories",
                            icon: "mdi mdi-tag-multiple-outline",
                            link: "/admin/panel/cost-categories",
                            parentId: "admin-panel",
                        },
                    ],
                }
            ]
            : []),
    ];

    return <React.Fragment>{menuItems}</React.Fragment>;
};

export default NavData;
